import React, { useCallback, useEffect, useState } from "react";
import { DollarSign, Store } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Skeleton } from "@/ui";

export default function AiCost() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/platform/assistant-cost");
      setData(r.data);
    } catch (e) { setError(friendlyError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const o = data?.overall || {};
  const shops = data?.shops || [];

  return (
    <Layout>
      <div style={{ marginBottom: 8 }}>
        <h1 className="t-h1">AI cost</h1>
        <p className="muted">Assistant cost per completed order. The number that decides whether the pricing works.</p>
      </div>

      {loading ? (
        <Card><Skeleton style={{ height: 180 }} /></Card>
      ) : error ? (
        <Card data-testid="ai-cost-error" className="text-sm text-rose-600">{error}</Card>
      ) : (
        <div className="stack" style={{ gap: 16 }}>
          <Card data-testid="ai-cost-overall" className="stack" style={{ gap: 12, background: "#0f172a", color: "#f8fafc" }}>
            <div className="flex items-center gap-2">
              <div className="rounded-full p-2" style={{ background: "var(--accent-wash)" }}><DollarSign size={18} style={{ color: "var(--wash-ink)" }} /></div>
              <div className="text-xs uppercase tracking-wide" style={{ color: "var(--wash-ink-2)" }}>Across all shops · last {data.windowDays} days</div>
            </div>
            <div className="text-5xl font-bold num" data-testid="ai-cost-per-order">{o.costPerOrder}</div>
            <div className="text-sm" style={{ color: "var(--wash-ink-2)" }}>
              {o.cost} of assistant credits over {o.completedOrders} completed order{o.completedOrders === 1 ? "" : "s"}.
            </div>
          </Card>

          <Card data-testid="ai-cost-shops" className="stack" style={{ gap: 12 }}>
            <div className="flex items-center gap-2">
              <Store size={18} className="text-slate-400" />
              <div className="text-lg font-semibold">Per shop</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ai-cost-table">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2 pr-3">Shop</th>
                    <th className="py-2 px-2 text-right">Assistant cost</th>
                    <th className="py-2 px-2 text-right">Orders</th>
                    <th className="py-2 pl-2 text-right">Cost / order</th>
                  </tr>
                </thead>
                <tbody>
                  {shops.map((s) => (
                    <tr key={s.shopId} className="border-b last:border-0" data-testid={`ai-cost-shop-${s.shopId}`}>
                      <td className="py-3 pr-3 font-medium">{s.shopName}</td>
                      <td className="py-3 px-2 text-right">{s.cost}</td>
                      <td className="py-3 px-2 text-right">{s.completedOrders}</td>
                      <td className="py-3 pl-2 text-right font-semibold text-emerald-700">{s.costPerOrder}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </Layout>
  );
}
